const escapeText = (value) =>
  String(value || 'Unknown file').replace(/[&<>"']/g, (character) => {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]
  })

// Owns one editor's complete selection/validation/upload cycle. The editor supplies
// UI callbacks; no temporary source is ever inserted into its document.
export function createRichTextVideoUpload({
  validate,
  upload,
  onBusy,
  onProgress,
  onSelection,
  onReset,
  onInsert,
  onError,
  onValidationError
}) {
  let alive = true
  let generation = 0
  let timer = null
  let phase = 'idle'
  let selection = []
  let abortController = null

  const active = (task) => alive && task === generation
  const reset = () => {
    generation += 1
    selection = []
    abortController = null
    phase = 'idle'
    onProgress(0)
    onReset()
    onBusy(false)
  }
  const cancel = () => {
    generation += 1
    clearTimeout(timer)
    timer = null
    if (abortController) abortController.abort()
    if (alive && phase !== 'idle') reset()
  }

  const run = async (task, files) => {
    let fallbackMessage = 'Unable to validate the selected videos.'
    try {
      phase = 'validating'
      const validation = await validate(files)
      if (!active(task)) return
      if (validation.durationErrors.length || validation.totalSizeError) {
        phase = 'reporting'
        if (validation.durationErrors.length) {
          const names = validation.durationErrors.map((item) => escapeText(item.name)).join('<br>')
          await onValidationError(
            `The video duration must be 120 seconds and below.<br><br>Files not meeting the duration requirement:<br><br>${names}`,
            'Video duration not supported'
          )
        }
        if (!active(task)) return
        if (validation.totalSizeError) {
          const contributions = validation.totalSizeError.files
            .map(
              (item) =>
                `${escapeText(item.name)} (${(Math.max(0, Number(item.size) || 0) / 1024 / 1024).toFixed(2)}MB)`
            )
            .join('<br>')
          await onValidationError(
            `The total size of selected videos exceeds the maximum limit of 500MB.<br><br>Files contributing to the limit:<br><br>${contributions}`,
            'Total upload size exceeded'
          )
        }
        return
      }

      phase = 'uploading'
      fallbackMessage = 'Video upload failed. Please try again.'
      abortController = typeof AbortController !== 'undefined' ? new AbortController() : null
      for (let index = 0; index < files.length; index += 1) {
        if (!active(task)) return
        const result = await upload(files[index], {
          signal: abortController ? abortController.signal : undefined,
          stopRequested: () => !active(task),
          onProgress: (progress) => {
            if (active(task)) {
              const percent = Math.max(0, Math.min(100, Number(progress) || 0))
              onProgress(Math.round(((index + percent / 100) / files.length) * 100))
            }
          }
        })
        if (!active(task)) return
        if (
          !result ||
          !/^https:\/\//i.test(result.src || '') ||
          (result.poster && !/^https:\/\//i.test(result.poster))
        ) {
          throw new Error('Only HTTPS video URLs can be saved')
        }
        onInsert(result)
      }
    } catch (error) {
      if (active(task)) onError((error && error.message) || fallbackMessage)
    } finally {
      if (active(task)) reset()
    }
  }

  return {
    select(file, fileList) {
      if (!alive || !file || !file.raw || phase === 'uploading' || phase === 'reporting') return
      const nextSelection = [...selection]
      for (const item of [...(fileList || []), file]) {
        if (item && item.raw && !nextSelection.some((selected) => selected.raw === item.raw)) {
          nextSelection.push(item)
        }
      }
      if (nextSelection.length === selection.length) return
      const wasIdle = phase === 'idle'
      selection = nextSelection
      phase = 'collecting'
      const task = ++generation
      clearTimeout(timer)
      if (wasIdle) {
        onBusy(true)
        onProgress(0)
      }
      onSelection([...selection])
      // Element emits once per selected file. Keep busy throughout this debounce
      // and allow later callbacks to replace an in-flight metadata validation.
      timer = setTimeout(() => {
        timer = null
        void run(
          task,
          selection.map((item) => item.raw)
        )
      }, 0)
    },
    cancel,
    destroy() {
      cancel()
      alive = false
    }
  }
}
